/**
 * Centralized accessors for the auth token + decoded user data.
 *
 * Backed by localStorage (not sessionStorage). iPad/iOS Safari can
 * suspend or discard a backgrounded tab's sessionStorage on memory
 * pressure, and sessionStorage is never shared across tabs of the
 * same origin — either one silently drops the token and bounces the
 * user back to /login. localStorage survives both.
 *
 * Every read/write of the "token" / "userData" keys should go
 * through here so the storage backend only has to change in one
 * place.
 */

import { jwtDecode } from "jwt-decode";

const TOKEN_KEY = "token";
const USER_DATA_KEY = "userData";

export const getToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

export const setToken = (token) => {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* ignore (e.g. private-mode storage quota) */
  }
};

export const getUserData = () => {
  try {
    return JSON.parse(localStorage.getItem(USER_DATA_KEY));
  } catch {
    return null;
  }
};

export const setUserData = (userData) => {
  try {
    localStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));
  } catch {
    /* ignore */
  }
};

export const clearToken = () => {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_DATA_KEY);
  } catch {
    /* ignore */
  }
};

/**
 * True only if a token is present AND its exp claim hasn't passed.
 *
 * Several "already authenticated?" guards (Login, SsoHandoff,
 * ProtectedRoutes) used to just check *presence* of a token. A stale
 * expired token left in storage would pass that check, so pages that
 * redirect back and forth based on presence-only (e.g. SsoHandoff ->
 * /login -> back to SsoHandoff) could loop forever without ever
 * hitting the network — nothing about that loop trips a 401. Checking
 * validity here closes that hole at the source.
 */
export const hasValidToken = () => {
  const token = getToken();
  if (!token) return false;
  try {
    const { exp } = jwtDecode(token);
    return !exp || exp * 1000 > Date.now();
  } catch {
    return false;
  }
};
